import React, { useEffect, useState } from 'react'

import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { PostsListFilter } from './PostsListFilter'
import { ClientColorSquare } from '../common/ClientColorSquare'
import { SmartTable } from '../common/table/Table'
import { SmartTableContext } from '../common/table/TableContext'
import { SmartTableHeadCell } from '../common/table/TableHeadCell'
import { SmartTableWrapper } from '../common/table/TableWrapper'
import { SortDir } from '../common/table/types'
import axiosClient from '../service/axiosClient'
import { PageableResponse, PagingRequest } from '../types/Common'
import PostDTO, { PostsFilter } from '../types/Post'
import { ReviewComment } from '../types/Review'
import SocialNetwork from '../types/SocialNetwork'

const socialNetworkOptions = [
    {value: SocialNetwork.INSTAGRAM, label: 'Instagram'},
    {value: SocialNetwork.FACEBOOK, label: 'Facebook'},
    {value: SocialNetwork.LINKED_IN, label: 'Linked In'},
    {value: SocialNetwork.TIKTOK, label: 'TikTok'},
]

export const PostsList = () => {
    const navigate = useNavigate()
    const {t, i18n} = useTranslation()

    const [filterInternal, setFilterInternal] = useState<PostsFilter>({
        clientId: '',
        hidden: false,
    })
    // Posts ticked for hiding, by id
    const [selected, setSelected] = useState<number[]>([])

    useEffect(() => {
        setSelected([])
    }, [filterInternal])
    const [expandedComments, setExpandedComments] = useState<Record<number, ReviewComment[]>>({})
    const [loadingComments, setLoadingComments] = useState<Record<number, boolean>>({})
    const [duplicatePost, setDuplicatePost] = useState<PostDTO | null>(null)
    const [duplicateNetwork, setDuplicateNetwork] = useState<SocialNetwork | ''>('')
    const [isDuplicating, setIsDuplicating] = useState(false)

    const toggleComments = async (postId: number) => {
        if (expandedComments[postId]) {
            setExpandedComments(prev => {
                const next = { ...prev }
                delete next[postId]
                return next
            })
            return
        }
        setLoadingComments(prev => ({ ...prev, [postId]: true }))
        try {
            const response = await axiosClient.get(`post/${postId}/review-comments`)
            setExpandedComments(prev => ({ ...prev, [postId]: response.data }))
        } catch (error) {
            console.error('Error loading comments:', error)
        } finally {
            setLoadingComments(prev => ({ ...prev, [postId]: false }))
        }
    }

    function loadDataFn(queryParams: PagingRequest) {
        console.log('queryParams', queryParams)
        const pageNumber = queryParams.pageStart || 0
        const pageSize = queryParams.pageSize || 15
        return new Promise<PageableResponse<PostDTO>>(
            (resolve) => {
                axiosClient.get('post', {
                    params: {
                        pageSize: pageSize,
                        pageStart: pageNumber,
                        clientId: filterInternal.clientId,
                        hidden: filterInternal.hidden,
                        sortDesc: queryParams.sortDesc,
                    }
                }).then(response => {
                    const totalElements = response.data.totalElements
                    const paginatedData = response.data.content
                    return resolve({
                        hasNextPage: true,
                        totalElements: totalElements,
                        content: paginatedData,
                    })
                }).catch(error => {
                    console.log('error', error)
                })
            }
        )
    }

    const dateLocale = i18n.language === 'en' ? 'en-US' : i18n.language === 'cs' ? 'cs-CZ' : 'sk-SK'

    // Deleting only hides a post from this list - the record stays, published or not
    async function hidePosts(ids: number[], reload: () => void) {
        if (ids.length === 0 || !confirm(t(ids.length === 1 ? 'confirm_delete_post' : 'confirm_hide_posts', {count: ids.length}))) {
            return
        }
        try {
            await axiosClient.put('post/hide', ids)
            toast.success(t(ids.length === 1 ? 'post_deleted_success' : 'posts_hidden_success', {count: ids.length}))
            setSelected([])
            reload()
        } catch (error) {
            console.error('Error hiding posts:', error)
            toast.error(t('posts_hide_error'))
        }
    }

    const toggleSelected = (id: number) => {
        setSelected(prev => prev.includes(id) ? prev.filter(selectedId => selectedId !== id) : [...prev, id])
    }

    async function openDuplicateModal(postId: number) {
        try {
            const response = await axiosClient.get('post/' + postId)
            setDuplicatePost(response.data)
            setDuplicateNetwork('')
        } catch (error) {
            console.error('Error loading post for duplication:', error)
            toast.error(t('post_load_error'))
        }
    }

    async function handleDuplicate(reload: () => void) {
        if (!duplicatePost || !duplicateNetwork) return
        setIsDuplicating(true)
        try {
            let linkedinAuthorUrn = ''
            let metaPageId = ''

            if (duplicateNetwork === SocialNetwork.LINKED_IN && duplicatePost.clientId) {
                try {
                    const response = await axiosClient.get(`linkedin/organizations?clientId=${duplicatePost.clientId}`)
                    if (response.data.length > 0) {
                        linkedinAuthorUrn = duplicatePost.linkedinAuthorUrn && response.data.some((a: any) => a.urn === duplicatePost.linkedinAuthorUrn)
                            ? duplicatePost.linkedinAuthorUrn
                            : response.data[0].urn
                    }
                } catch (err) {
                    console.error('Error fetching LinkedIn authors for duplication', err)
                }
            }

            if ((duplicateNetwork === SocialNetwork.FACEBOOK || duplicateNetwork === SocialNetwork.INSTAGRAM) && duplicatePost.clientId) {
                try {
                    const response = await axiosClient.get(`meta/pages?clientId=${duplicatePost.clientId}`)
                    let availablePages = response.data
                    if (duplicateNetwork === SocialNetwork.INSTAGRAM) {
                        availablePages = availablePages.filter((p: any) => p.hasInstagram)
                    }
                    if (availablePages.length > 0) {
                        metaPageId = duplicatePost.metaPageId && availablePages.some((p: any) => p.id === duplicatePost.metaPageId)
                            ? duplicatePost.metaPageId
                            : availablePages[0].id
                    }
                } catch (err) {
                    console.error('Error fetching Meta pages for duplication', err)
                }
            }

            const request = {
                title: duplicatePost.title ?? '',
                description: duplicatePost.description ?? '',
                clientId: duplicatePost.clientId,
                postType: duplicatePost.postType,
                socialNetwork: duplicateNetwork,
                publishFrequency: duplicatePost.publishFrequency,
                // A copy has not been approved by anyone yet
                postApprovalStatus: 'NEW',
                postStatus: 'NOT_PUBLISHED',
                photo: duplicatePost.photo ?? '',
                showInCalendar: duplicatePost.showInCalendar,
                hashTags: (duplicatePost.hashTags ?? '').replace(/^,+|,+$/g, ''),
                publishAt: duplicatePost.publishAt ?? '',
                publishFrom: duplicatePost.publishFrom ?? '',
                publishUntil: duplicatePost.publishUntil ?? '',
                linkedinAuthorUrn,
                metaPageId,
            }
            await axiosClient.post('post', request)
            toast.success(t('post_duplicated_success'))
            setDuplicatePost(null)
            reload()
        } catch (error) {
            console.error('Error duplicating post:', error)
            toast.error(t('post_save_error'))
        } finally {
            setIsDuplicating(false)
        }
    }

    return <>
        <PostsListFilter filter={filterInternal} setFilter={setFilterInternal}/>
        {/*<div className="rounded-content-body mb-4">*/}
        <SmartTableWrapper<PostDTO, PostsFilter>
            initialPageSize={10}
            // Newest first; the date header turns it to the oldest
            initialSortBy="date"
            initialSortDir={SortDir.Desc}
            filter={filterInternal}
            loadDataFn={loadDataFn}>
            <SmartTableContext.Consumer>
                {api => {
                    const pageIds = (api.entries as PostDTO[]).map(entry => entry.id).filter((id): id is number => !!id)
                    const allOnPageSelected = pageIds.length > 0 && pageIds.every(id => selected.includes(id))

                    return (<>
                    {!filterInternal.hidden && (
                        <div className="d-flex justify-content-end mb-2">
                            <button
                                type="button"
                                className="amani-primary"
                                disabled={selected.length === 0}
                                onClick={() => hidePosts(selected, () => api.load())}>
                                <i className="bi bi-trash me-1"></i>
                                {t('delete_selected', {count: selected.length})}
                            </button>
                        </div>
                    )}
                    <SmartTable<PostDTO>
                        onRowClick={(item) => {
                            // navigate('/posts/edit/' + item.id)
                        }}
                        HeadRow={
                            <>
                                {!filterInternal.hidden && (
                                    <SmartTableHeadCell>
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            title={t('select_all')}
                                            checked={allOnPageSelected}
                                            onChange={(e) => {
                                                setSelected(prev => e.target.checked
                                                    ? Array.from(new Set([...prev, ...pageIds]))
                                                    : prev.filter(id => !pageIds.includes(id)))
                                            }}/>
                                    </SmartTableHeadCell>
                                )}
                                <SmartTableHeadCell>
                                    {t('client')}
                                </SmartTableHeadCell>
                                <SmartTableHeadCell>
                                    {t('post_title')}
                                </SmartTableHeadCell>
                                <SmartTableHeadCell>
                                    {t('type')}
                                </SmartTableHeadCell>
                                <SmartTableHeadCell>
                                    {t('network')}
                                </SmartTableHeadCell>
                                <SmartTableHeadCell sort="date">
                                    {t('date')}
                                </SmartTableHeadCell>
                                <SmartTableHeadCell>
                                    {t('time')}
                                </SmartTableHeadCell>
                                <SmartTableHeadCell>
                                    {t('status')}
                                </SmartTableHeadCell>
                                <SmartTableHeadCell
                                    className="text-center">
                                    {t('approval')}
                                </SmartTableHeadCell>
                                <SmartTableHeadCell
                                    className="text-center">
                                    {t('action')}
                                </SmartTableHeadCell>
                            </>
                        }
                        renderRow={(item) => {
                            return (
                                <>
                                    {!filterInternal.hidden && (
                                        <td>
                                            {item.id && (
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selected.includes(item.id)}
                                                    onChange={() => item.id && toggleSelected(item.id)}/>
                                            )}
                                        </td>
                                    )}
                                    <td><ClientColorSquare
                                        clientId={item.clientId}/>{`${item.clientFirstName} ${item.clientLastName}`}
                                    </td>
                                    <td>{item.title}</td>
                                    <td className={'text-capitalize'}>{item.postType ?? '-'}</td>
                                    <td><i
                                        className={`bi bi-${item.socialNetwork === SocialNetwork.FACEBOOK ? 'facebook' : item.socialNetwork === SocialNetwork.INSTAGRAM ? 'instagram' : item.socialNetwork === SocialNetwork.TIKTOK ? 'tiktok' : 'linkedin'}`}></i>
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{new Date(item.publishFrom).toLocaleDateString(dateLocale)}{item.publishUntil ? ' - ' + new Date(item.publishUntil).toLocaleDateString(dateLocale) : null}</td>
                                    <td>{item.publishAt.slice(0, 5)}</td>
                                    <td>
                                        {item.postStatus === 'PUBLISHED' && <span className="badge bg-success">{t('published')}</span>}
                                        {item.postStatus === 'SCHEDULED' && <span className="badge bg-info text-dark">{t('scheduled')}</span>}
                                        {item.postStatus === 'NOT_PUBLISHED' && <span className="badge bg-danger">{t('not_published')}</span>}
                                        {!item.postStatus && <span className="text-muted">-</span>}
                                    </td>
                                    <td>
                                        {item.postApprovalStatus === 'NEW' && <span className={'badge bg-secondary'}>{t('post_new')}</span>}
                                        {item.postApprovalStatus === 'APPROVED' && <span className={'badge bg-success'}>{t('approved')}</span>}
                                        {item.postApprovalStatus === 'CHANGE_REQUIRED' && <span className={'badge bg-warning text-dark'}>{t('change_required')}</span>}
                                        {item.postApprovalStatus === 'DECLINED' && <span className={'badge bg-danger'}>{t('declined')}</span>}
                                        {!item.postApprovalStatus && <span className="text-muted">-</span>}
                                    </td>
                                    <td className="text-center" style={{ verticalAlign: 'middle' }}>
                                        <div className="d-flex gap-2 justify-content-center">
                                            <Link className={'table-action-link'} to={'/app/posts/edit/' + item.id} title={t('edit')}>
                                                <i className="bi bi-pencil primary-text-color"></i>
                                            </Link>
                                            {!filterInternal.hidden && (
                                                <a href={'#'} className={'table-action-link'} onClick={(e) => {
                                                    e.preventDefault()
                                                    item.id && hidePosts([item.id], () => api.load())
                                                }} title={t('delete')}>
                                                    <i className="bi bi-trash primary-text-color"></i>
                                                </a>
                                            )}
                                            {item.id && (
                                                <a href="#" className="table-action-link" onClick={(e) => {
                                                    e.preventDefault()
                                                    toggleComments(item.id!)
                                                }} title={t('show_comments')}>
                                                    <i className={`bi ${expandedComments[item.id!] ? 'bi-chat-dots-fill' : 'bi-chat-dots'} primary-text-color`}></i>
                                                </a>
                                            )}
                                            {item.id && (
                                                <a href="#" className="table-action-link" onClick={(e) => {
                                                    e.preventDefault()
                                                    openDuplicateModal(item.id!)
                                                }} title={t('duplicate_post')}>
                                                    <i className="bi bi-copy primary-text-color"></i>
                                                </a>
                                            )}
                                        </div>
                                        {item.id && expandedComments[item.id] && (
                                            <div className="mt-2 text-start" style={{ maxWidth: '250px', margin: '0 auto' }}>
                                                {expandedComments[item.id].length === 0 ? (
                                                    <small className="text-muted">{t('no_comments')}</small>
                                                ) : (
                                                    expandedComments[item.id].map(comment => (
                                                        <div key={comment.id} className="mb-1 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '0.8rem' }}>
                                                            <div className="d-flex align-items-center gap-1 mb-1">
                                                                {comment.reviewAction === 'APPROVED' ? (
                                                                    <span className="badge bg-success" style={{ fontSize: '0.65rem' }}>{t('approved')}</span>
                                                                ) : (
                                                                    <span className="badge bg-warning text-dark" style={{ fontSize: '0.65rem' }}>{t('change_required')}</span>
                                                                )}
                                                                <small className="text-muted">
                                                                    {new Date(comment.createdAt).toLocaleDateString(dateLocale)}
                                                                </small>
                                                            </div>
                                                            {comment.comment && <small>{comment.comment}</small>}
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                        {item.id && loadingComments[item.id] && (
                                            <small className="text-muted">{t('loading')}</small>
                                        )}
                                    </td>

                                </>
                            )
                        }}
                    />
                    </>)
                }}</SmartTableContext.Consumer>

        {duplicatePost && (
            <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{t('duplicate_post')}</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => setDuplicatePost(null)}
                            ></button>
                        </div>
                        <div className="modal-body">
                            <p>{t('duplicate_post_description')}</p>
                            <div className="form-group">
                                <label htmlFor="duplicateNetwork">{t('social_network')}</label>
                                <select
                                    id="duplicateNetwork"
                                    className="form-select"
                                    value={duplicateNetwork}
                                    onChange={(e) => setDuplicateNetwork(e.target.value as SocialNetwork)}
                                >
                                    <option value="">{t('select')}</option>
                                    {socialNetworkOptions
                                        .filter(opt => opt.value !== duplicatePost.socialNetwork)
                                        .map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                </select>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setDuplicatePost(null)}
                            >
                                {t('cancel')}
                            </button>
                            <SmartTableContext.Consumer>
                                {api => (
                                    <button
                                        type="button"
                                        className="amani-primary"
                                        onClick={() => handleDuplicate(() => api.load())}
                                        disabled={!duplicateNetwork || isDuplicating}
                                    >
                                        {isDuplicating ? t('loading') : t('duplicate')}
                                    </button>
                                )}
                            </SmartTableContext.Consumer>
                        </div>
                    </div>
                </div>
            </div>
        )}

        </SmartTableWrapper>
    </>
}
